import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';

import {cn} from '@/utils/tailwind';

type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
	/** Heading level. This will translate into any h1-6 level unless `asChild` is defined */
	level?: 1 | 2 | 3 | 4 | 5 | 6;
	/** Adds margin-bottom */
	spacing?: boolean;
	/**
	 * Change the default rendered element for the one passed as a child, merging their props and behavior.
	 * @default false
	 */
	asChild?: boolean;
	size?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

/** Use `Heading` to render h1-6 elements with heading text styles.  */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
	({level = 1, spacing = false, className, asChild, size, ...rest}, ref) => {
		const Component = asChild ? Slot : `h${level ?? 1}`;

		let spacingClass = '';

		let defaultSize: HeadingProps['size'] = 'h1';
		switch (level) {
			case 6:
				defaultSize = 'h6';
				break;
			case 5:
				defaultSize = 'h5';
				break;
			case 4:
				defaultSize = 'h4';
				break;
			case 3:
				defaultSize = 'h3';
				break;
			case 2:
				defaultSize = 'h2';
				break;
			case 1:
				defaultSize = 'h1';
				break;
		}

		if (spacing) {
			spacingClass = 'mb-2';
		}

		size = size ?? defaultSize;

		return (
			<Component
				ref={ref}
				className={cn([
					'font-display',
					'font-bold',
					size === 'h1' && ['md:text-heading-xl text-4xl'],
					size === 'h2' && ['md:text-heading-lg text-3xl'],
					size === 'h3' && ['md:text-heading-md text-2xl'],
					size === 'h4' && ['md:text-heading-sm text-xl'],
					size === 'h5' && ['md:text-heading-xs text-lg'],
					size === 'h6' && ['text-heading-2xs'],
					spacingClass,
					className,
				])}
				{...rest}
			/>
		);
	},
);

Heading.displayName = 'Heading';
